/**
 * The `+` rule, as one component (`KLQ-20260821-001` §D).
 *
 * **No number on this screen claims to be a total.** There is no counts endpoint and this wave
 * does not add one, so every number here is derived from the pages the client actually holds —
 * which is a fact about the cache, not about the project. The `+` is what makes the difference
 * visible: `50+ tasks` while the cursor has more to give, `137 tasks` once it is exhausted.
 *
 * It is a component rather than a helper because the rule has to hold in three places that
 * would otherwise each re-derive it: the register header, the saved-filter chips, and the
 * board's four column headers in the next wave. A rule every call site re-derives is a rule
 * that holds until the first call site forgets it (retro rule 8), and the shape that forgets
 * it — a bare `{tasks.length}` — is one character shorter to write than the shape that does
 * not.
 *
 * The upgrade path costs no component: when `GET /projects/{id}/tasks/counts` lands, `count`
 * becomes the server's capped exact count and `hasMore` its *"more than the cap"* flag. The
 * `+` becomes real and nothing here changes shape.
 */

import { useMessages } from '@/lib/i18n';

import styles from './Tasks.module.css';

export interface LoadedCountProps {
  /** Rows the client is holding. Never a total, which is the whole point of this file. */
  readonly count: number;
  /** The last page's `has_more`. `true` means the cursor is not exhausted. */
  readonly hasMore: boolean;
  /**
   * Just the number, for a slot whose label already says what is being counted — a status
   * column header, or a filter chip that carries its own word.
   */
  readonly bare?: boolean;
}

export function LoadedCount({ count, hasMore, bare = false }: LoadedCountProps) {
  const t = useMessages('tasks');
  const key = bare
    ? hasMore
      ? 'count.bareMore'
      : 'count.bare'
    : hasMore
      ? 'count.tasksMore'
      : 'count.tasks';
  return <span className={styles.count}>{t(key, { count })}</span>;
}
